import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import React, { useState, useEffect, useRef } from 'react';

interface NavbarProps {
  onOpenBooking: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenBooking }) => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const { content, t, isMenuOpen, setIsMenuOpen, isTheatreMode, is3DInspectionActive } = useContent();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: t('nav_about'), href: '/#about', id: 'about' },
    { label: t('nav_services'), href: '/#services', id: 'services' },
    { label: t('nav_projects'), href: '/#projects', id: 'projects' },
    { label: t('nav_tech'), href: '/#technologies', id: 'technologies' },
    { label: t('nav_team'), href: '/#team', id: 'team' },
    { label: t('nav_contact'), href: '/#contact', id: 'contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 50);

      if (location.pathname === '/') {
        // "Eye Level" Strategy: Detect which section is currently under the user's focus line
        // We set the focus line at 40% of the viewport height
        const eyeLevel = window.innerHeight * 0.4;
        let newActiveSection = '';

        // If we are still in the Hero section (top of page), force no selection
        // Hero is usually 100vh. We assume user "entered" About when they scroll past 50% of screen.
        if (window.scrollY > window.innerHeight * 0.5) {
          // Iterate through sections to find which one crosses the eye level
          for (const item of navItems) {
            const element = document.getElementById(item.id);
            if (element) {
              const rect = element.getBoundingClientRect();

              // A section is active if its top is above the eye level 
              // AND its bottom is below the eye level
              if (rect.top <= eyeLevel && rect.bottom > eyeLevel) {
                newActiveSection = item.id;
                break; // Found the active section, stop checking
              }
            }
          }
        }

        // Special override for bottom of the page to ensure Contact is highlighted
        // when we reach the footer, even if the section is short
        if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
          newActiveSection = 'contact';
        }

        setActiveSection(newActiveSection);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.style.overflow = 'unset';
    };
  }, [location.pathname, isMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setIsMenuOpen(false);
    const id = href.includes('#') ? href.split('#')[1] : null;

    if (location.pathname === '/' && id) {
      const element = document.getElementById(id);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', `/${id}`);
      }
    }
  };

  const handleLogoClick = () => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-[160] transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${isTheatreMode || is3DInspectionActive ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
        } ${scrolled || isMenuOpen ? 'bg-[#1a141f] py-2 border-b border-white/5 shadow-lg' : 'bg-[#1a141f] py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex justify-between items-center h-20">
            <Link
              to="/"
              className="flex items-center group relative z-[170]"
              onClick={handleLogoClick}
            >
              <img
                src={content.logo}
                alt="Saltium Games"
                width="94"
                height="64"
                fetchPriority="high"
                className="h-16 sm:h-20 w-auto transition-transform group-hover:scale-110"
              />
            </Link>

            <div className="hidden lg:flex items-center space-x-5">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`relative font-gaming font-bold uppercase tracking-widest text-sm transition-all py-2 px-1 flex items-center h-full ${activeSection === item.id ? 'text-accent scale-105' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-accent transition-all duration-300 ${activeSection === item.id ? 'w-full opacity-100' : 'w-0 opacity-0'}`}></span>
                </Link>
              ))}

              <div className="h-4 w-px bg-gray-800 mx-1"></div>

              <button
                id="cta-navbar-book"
                onClick={onOpenBooking}
                className="btn-primary py-2.5 px-6 text-xs whitespace-nowrap"
              >
                {t('btn_book')}
              </button>
            </div>

            <div className="lg:hidden flex items-center z-[170]">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-accent p-2 focus:outline-none transition-transform active:scale-90"
                aria-label="Toggle Menu"
              >
                {isMenuOpen ? (
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 w-full h-screen bg-[#1a141f] z-[150] transition-all duration-300 flex flex-col lg:hidden ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}>
        <div className="flex-grow flex flex-col overflow-y-auto px-6 pt-36 pb-12">
          <div className="flex flex-col space-y-4 mb-10">
            {navItems.map((item, idx) => (
              <Link
                key={item.id}
                to={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`text-4xl font-gaming font-extrabold uppercase tracking-tight transition-all duration-300 transform ${isMenuOpen ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}
                style={{ transitionDelay: `${idx * 75}ms` }}
              >
                <span className={activeSection === item.id ? 'text-accent' : 'text-white'}>
                  {item.label}
                </span>
              </Link>
            ))}
          </div>

          <div className="h-px w-full bg-gray-900 mb-8"></div>

          <div className="mt-auto space-y-4">
            <button
              id="cta-navbar-mobile-book"
              onClick={() => {
                setIsMenuOpen(false);
                onOpenBooking();
              }}
              className="w-full bg-accent text-white py-5 rounded-2xl font-bold uppercase tracking-widest text-sm transition-all shadow-xl active:scale-95"
            >
              {t('btn_book')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};